import { inject, Injectable } from '@angular/core';
import { selectCreateViewStateViewState } from '@common/store/createviewstate/createviewstate.selectors';
import { ApplicationActions } from '@common/store/navigation/actions/application.actions';
import { ViewStateActions, ViewStateApiActions } from '@common/store/navigation/actions/viewstate.actions';
import { selectCurrentApplication, selectCurrentItem } from '@common/store/navigation/selectors/navigation.selectors';
import { selectCurrentViewState, selectViewStateFilters } from '@common/store/navigation/selectors/viewstate.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { environment } from '@src/environments/environment';
import { ViewStateFilterSerializer } from '@src/suite/common/data-models/serializers/serializers';
import { AppName, TeamScope } from '@src/suite/common/enums/enums';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { CustomCardFieldsService } from '@src/suite/common/services/custom-card-fields-service';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { IndexedDBService } from '@src/suite/common/services/indexeddb-service';
import { PublicationChannelsService } from '@src/suite/common/services/publication-channels-service';
import { ViewStateService } from '@src/suite/common/services/view-state-service';
import { CastActions, CastApiActions } from '@src/suite/common/store/cast/cast.actions';
import { selectGroupSort, selectPSTGroupPrivileges } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ListActions } from '@src/suite/common/store/list/list.actions';
import { NavigationApiActions } from '@src/suite/common/store/navigation/actions/navigation.actions';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { PublicationRecordActions, RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { isFilterDefinitionValueSet } from '@src/suite/common/utils/filter-utils';
import {
    getGroupByOptions,
    getLayoutOptions,
    getSortByOptions,
    isMultiFeedViewStateRecord,
    isPublicationViewStateRecord,
    isSearchViewStateRecord,
    isSingleViewStateRecord,
    isTopicViewStateRecord,
} from '@src/suite/common/utils/viewstate-utils';
import { WireActions, WireApiActions } from '@src/suite/discover/store/actions/discover.actions';
import { catchError, concatMap, from, map, mergeMap, of, switchMap } from 'rxjs';

import { CreateViewStateActions } from '../../createviewstate/createviewstate.actions';

@Injectable()
export class ViewStateEffects {
    private actions$ = inject(Actions);
    private store = inject(Store);
    private viewStateService = inject(ViewStateService);
    private publicationChannelsService = inject(PublicationChannelsService);
    private customCardFieldsService = inject(CustomCardFieldsService);
    private globalTablesService = inject(GlobalTablesService);
    private db = inject(IndexedDBService);

    setCurrentViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateActions.setCurrentViewState),
            concatLatestFrom(() => [this.store.select(selectI18N), this.store.select(selectPSTGroupPrivileges), this.store.select(selectGroupSort)]),
            switchMap(([action, i18n, pstGroupPrivs, groupSort]) => {
                const vs = action.vs ? { ...action.vs } : null;
                if (!vs) return of(ViewStateActions.setCurrentViewStateSuccess({ vs: null, layout: null, sortBy: null }));
                const layout = getLayoutOptions(vs.PSTViewStateType, i18n, pstGroupPrivs);
                const sortBy = getSortByOptions(vs.PSTViewStateType, groupSort, i18n);
                const groupBy = getGroupByOptions(vs.PSTViewStateType, groupSort, i18n);
                const vsFilter = ViewStateFilterSerializer.instance(this.globalTablesService).initialize(vs.PSTViewStateType);

                if (isSingleViewStateRecord(vs) || isPublicationViewStateRecord(vs)) {
                    if (isTopicViewStateRecord(vs) || isSearchViewStateRecord(vs) || isPublicationViewStateRecord(vs)) {
                        // Set default custom card fields
                        if (!(vs as StorytellingSingleViewStateRecord).PSTViewStateTopicCardCustomItems || !Array.isArray((vs as StorytellingSingleViewStateRecord).PSTViewStateTopicCardCustomItems)) {
                            (vs as StorytellingSingleViewStateRecord).PSTViewStateTopicCardCustomItems = Object.assign([], this.customCardFieldsService.CustomCardFields);
                            (vs as StorytellingSingleViewStateRecord).isViewStateCustomCardItemsBlank = true;
                        }
                    }
                }
                if (isMultiFeedViewStateRecord(vs)) {
                    const sequence = (vs as StorytellingMultiFeedViewStateRecord).PSTViewStateSequence;
                    return this.viewStateService.getViewStateRecordByGuids(sequence.ViewStates?.map(item => item.Guid)).pipe(
                        mergeMap(res => {
                            if (res.status) {
                                (vs as StorytellingMultiFeedViewStateRecord).PSTMultiFeedViewStates = res.data;
                                return of(ViewStateActions.setCurrentViewStateSuccess({ vs: vs, layout, sortBy, groupBy }));
                            }
                            return of(ViewStateActions.setCurrentViewStateError({ error: res.message }));
                        })
                    );
                }
                if (isPublicationViewStateRecord(vs)) {
                    return from(this.publicationChannelsService.getPublicationChannelRecord((vs as StorytellingPublicationViewStateRecord).PSTPublicationChannelGUID)).pipe(
                        mergeMap(res => {
                            if (res.status) {
                                (vs as StorytellingPublicationViewStateRecord).publicationChannel = res.data;
                                return [
                                    ViewStateActions.setCurrentViewStateSuccess({ vs: vs, layout }),
                                    // CastApiActions.loadPublicationLists({ vs }),
                                    PublicationRecordActions.updatePublicationChannels({ records: [res.data] }),
                                ];
                            }
                            return of(ViewStateActions.setCurrentViewStateError({ error: res.message }));
                        })
                    );
                }
                return of(ViewStateActions.setCurrentViewStateSuccess({ vs, layout, sortBy, groupBy, vsFilter }));
            })
        );
    });

    setCurrentViewStateSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateActions.setCurrentViewStateSuccess),
            concatLatestFrom(() => [this.store.select(selectCurrentApplication), this.store.select(selectCurrentViewState)]),
            switchMap(([_, application, vs]) => {
                if (!vs) {
                    return of(WireApiActions.stopPolling({ restart: false, vs: null, interval: null }));
                } else {
                    if (application === AppName.WASTEBIN) {
                        return [ListActions.setLoading({ isLoading: true }), ListActions.clearNewListGuids(), RecordHttpActions.loadWastebinObjects({ vs })];
                    }
                    if (application === AppName.TEMPLATES) {
                        return [ListActions.setLoading({ isLoading: true }), ListActions.clearNewListGuids(), RecordHttpActions.loadTemplatesObjects({ vs })];
                    }
                    if (application === AppName.DISCOVER) {
                        const actions = [];
                        if (vs.PSTViewStateType === ViewStateTypes.WIRESEARCH) {
                            actions.push(WireActions.loading({ isLoading: true }));
                            actions.push(WireActions.searchWires({ vs: vs as StorytellingSearchViewStateRecord }));
                        } else {
                            actions.push(
                                WireApiActions.startPolling({
                                    interval: this.getPollInvervalInSeconds(),
                                })
                            );
                        }
                        return actions;
                    }
                    if (application === AppName.LIVE) {
                        const actions: Action[] = [ListActions.setLoading({ isLoading: true }), ListActions.clearNewListGuids()];
                        if (vs.PSTViewStateType !== ViewStateTypes.SINGLEITEM) {
                            actions.push(RecordHttpActions.loadRundowns({ vs }));
                        }
                        return actions;
                    }
                    if (application === AppName.PLAYBOOK) {
                        if (vs.PSTViewStateType !== ViewStateTypes.SINGLEITEM) {
                            // loadRundowns is no longer dispatched separately here for SEARCH.
                            // loadPlaybookObjects$ dispatches loadRundowns itself as the final step of its chain
                            // when the view state is SEARCH, guaranteeing topics load before rundowns and that
                            // startPolling only fires after both are complete.
                            return [ListActions.setLoading({ isLoading: true }), ListActions.clearNewListGuids(), RecordHttpActions.loadPlaybookObjects({ vs })];
                        }
                    }
                    if (application === AppName.CAST && vs && vs.PSTViewStateType === ViewStateTypes.PUBLICATION) {
                        const layout = getLayoutOptions(vs.PSTViewStateType, this.store.selectSignal(selectI18N)(), this.store.selectSignal(selectPSTGroupPrivileges)());
                        return [
                            CastApiActions.loadPublicationList({ vs: vs as StorytellingPublicationViewStateRecord, setMaxPublicationRevisionNumber: true }),
                            CastApiActions.loadViewStates(),
                            PlaybookApiActions.loadTopicTemplates(),
                            CastActions.setLoadingList({ listLoading: true }),
                            ViewStateActions.updateViewStateLayout({ layout: vs.PSTViewStateLayout.Value || layout.Default }),
                            (vs as StorytellingPublicationViewStateRecord).PSTViewStateGUID
                                ? CastActions.setCurrentViewState({ guid: (vs as StorytellingPublicationViewStateRecord).PSTViewStateGUID })
                                : null,
                        ].filter(x => x);
                    }
                    return of(ApplicationActions.noOpAction());
                }
            })
        );
    });

    crudViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.updateNavigationItemSuccess),
            concatLatestFrom(() => [this.store.select(selectCurrentViewState), this.store.select(selectCreateViewStateViewState)]),
            concatMap(([_, vs, updated]) => {
                if (vs?.Guid === updated?.Guid) {
                    return [ViewStateActions.setCurrentViewState({ vs: updated }), CreateViewStateActions.resetState()];
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    addViewStateFilter$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateActions.addViewStateFilters, ViewStateActions.removeViewStateFilter),
            concatLatestFrom(() => [this.store.select(selectViewStateFilters)]),
            mergeMap(([action, filters]) => {
                // Check if added/removed filters has value set, if set - reload
                const reload: boolean = filters?.some(f => [...(action.added ?? []), ...(action.removed ?? [])].includes(f.ID) && isFilterDefinitionValueSet(f));
                if (reload) {
                    return of(ViewStateActions.updateViewStateFilter({}));
                }
                // Don't reload
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    updateViewStateFilter$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateActions.updateViewStateFilter, ViewStateActions.adjustDateRange),
            concatLatestFrom(() => [this.store.select(selectCurrentApplication), this.store.select(selectCurrentViewState)]),
            mergeMap(([_, application, vs]) => {
                if (application === AppName.DISCOVER) {
                    return of(
                        WireApiActions.stopPolling({
                            restart: true,
                            vs: vs,
                            interval: this.getPollInvervalInSeconds(),
                        })
                    );
                }
                if (application === AppName.WASTEBIN) {
                    return [ListActions.setLoading({ isLoading: true }), ListActions.clearNewListGuids(), RecordHttpActions.loadWastebinObjects({ vs })];
                }
                if (application === AppName.TEMPLATES) {
                    return [ListActions.setLoading({ isLoading: true }), ListActions.clearNewListGuids(), RecordHttpActions.loadTemplatesObjects({ vs })];
                }
                if (application === AppName.LIVE) {
                    return [ListActions.setLoading({ isLoading: true }), ListActions.clearNewListGuids(), RecordHttpActions.loadRundowns({ vs })];
                }
                if (application === AppName.PLAYBOOK) {
                    if (vs.PSTViewStateType !== ViewStateTypes.SINGLEITEM) {
                        // loadRundowns is no longer dispatched separately here for SEARCH.
                        // loadPlaybookObjects$ dispatches loadRundowns itself as the final step of its chain
                        // when the view state is SEARCH, guaranteeing topics load before rundowns and that
                        // startPolling only fires after both are complete.
                        return [ListActions.setLoading({ isLoading: true }), ListActions.clearNewListGuids(), RecordHttpActions.loadPlaybookObjects({ vs })];
                    }
                }
                if (application === AppName.CAST && vs && vs.PSTViewStateType === ViewStateTypes.PUBLICATION) {
                    // return of(CastApiActions.loadPublicationList({ vs }));
                    return [
                        CastApiActions.loadPublicationList({ vs: vs as StorytellingPublicationViewStateRecord, setMaxPublicationRevisionNumber: true }),
                        CastActions.setLoadingList({ listLoading: true }),
                    ];
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    applyTeamCustomCardFields$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateApiActions.applyTeamCustomCardFields),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            mergeMap(([action, vs]) => {
                if (isTopicViewStateRecord(vs) || isSearchViewStateRecord(vs) || isPublicationViewStateRecord(vs)) {
                    return from(this.viewStateService.updateTeamCustomCardFields(action.fields, action.team)).pipe(
                        map(() => ViewStateApiActions.applyTeamCustomCardFieldsSuccess({ fields: this.customCardFieldsService.CustomCardFields })),
                        catchError(error => of(ViewStateApiActions.applyTeamCustomCardFieldsError({ error: error })))
                    );
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    loadViewStateRecords$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateApiActions.loadViewStateRecords),
            concatLatestFrom(() => this.store.select(selectCurrentApplication)),
            mergeMap(([action, app]) => {
                const application = action.application || app;
                return from(this.viewStateService.loadViewStateRecords(application, action.group, null, action.creators)).pipe(
                    map(res => {
                        if (res.status) return ViewStateApiActions.loadViewStateRecordsSuccess({ application: application, viewstates: res.data });
                        return ViewStateApiActions.loadViewStateRecordsError({ error: res.message });
                    }),
                    catchError(error => of(ViewStateApiActions.loadViewStateRecordsError({ error })))
                );
            })
        );
    });

    deleteViewStateRecord$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateApiActions.deleteViewStateRecord),
            concatLatestFrom(() => this.store.select(selectCurrentApplication)),
            mergeMap(([action, application]) => {
                if (application === AppName.DISCOVER) {
                    return this.viewStateService.deleteViewStateDiscoverRecord(action.vs).pipe(
                        map(res => ViewStateApiActions.deleteViewStateRecordSuccess({ status: res.status })),
                        catchError(error => of(ViewStateApiActions.deleteViewStateRecordError({ error: error })))
                    );
                } else {
                    return this.viewStateService.deleteViewStateRecord(action.vs).pipe(
                        map(res => ViewStateApiActions.deleteViewStateRecordSuccess({ status: res.status })),
                        catchError(error => of(ViewStateApiActions.deleteViewStateRecordError({ error: error })))
                    );
                }
            })
        );
    });

    deleteViewStateRecordSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateApiActions.deleteViewStateRecordSuccess),
            map(() => {
                return ViewStateApiActions.loadViewStateRecords({ group: TeamScope.ALL });
            })
        );
    });

    storeSystemViewState$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    ViewStateActions.setCurrentViewStateSuccess,
                    ViewStateActions.updateViewStateLayout,
                    ViewStateActions.updateViewStateGroupBy,
                    ViewStateActions.updateViewStateSortBy,
                    ViewStateActions.updateViewStateSortBySortOrder,
                    ViewStateActions.updateViewStateGroupBySortOrder,
                    ViewStateActions.addViewStateFilters,
                    ViewStateActions.removeViewStateFilter,
                    ViewStateActions.updateViewStateFilter,
                    ViewStateActions.updateViewStateFilterState,
                    ViewStateActions.updateViewStateCustomCardFields,
                    ViewStateActions.updateViewStateRevisionNumber,
                    ViewStateActions.updateViewStateMaxRevisionNumber,
                    ViewStateActions.updateViewStateCurrentMapCenter,
                    ViewStateActions.updateViewStateCurrentMapZoom,
                    ViewStateActions.toggleViewStateShowTeamLocations,
                    ViewStateActions.toggleViewStateLockMapLocation,
                    ViewStateActions.toggleViewStateZoomToCoverage,
                    ViewStateActions.toggleFilterState,
                    ViewStateActions.toggleFilterElevation
                ),
                concatLatestFrom(() => [this.store.select(selectCurrentItem), this.store.select(selectCurrentViewState)]),
                concatMap(([_, item, vs]) => {
                    if (item?.System && item?.ViewState) {
                        const record = { ...this.viewStateService.nomWebServicesJson.convertor.serialize(vs), DBGuid: item.Guid };
                        return this.db.update(record);
                    }
                    return of(null);
                })
            );
        },
        { dispatch: false }
    );

    resetNodeExpandedState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                ViewStateActions.updateViewStateLayout,
                ViewStateActions.updateViewStateGroupBy,
                ViewStateActions.updateViewStateSortBy,
                ViewStateActions.updateViewStateSortBySortOrder,
                ViewStateActions.updateViewStateGroupBySortOrder,
                ViewStateActions.addViewStateFilters,
                ViewStateActions.removeViewStateFilter,
                ViewStateActions.updateViewStateFilter,
                ViewStateActions.setCurrentViewState,
                ViewStateActions.updateViewStateViewStateGuid
            ),
            map(() => {
                return ListActions.resetNodeExpandedState();
            })
        );
    });

    private _pollInterval: number;
    public getPollInvervalInSeconds() {
        if (this._pollInterval) {
            return this._pollInterval;
        }

        this._pollInterval = environment.solr_poll_interval_seconds ? environment.solr_poll_interval_seconds * 1000 : 10000;
        return this._pollInterval;
    }
}
